// =====================================================================
// src/lib/precheckin.js — o que as duas telas do pré-check-in decidem, sem DOM
//
// Puro. Serve à página do HÓSPEDE (precheckin.html: o token do link, o
// formulário de até 3 pessoas, a validação antes da rede) e à tela da
// RECEPÇÃO (o estado de cada reserva no painel, o link montado, a
// comparação do envio com a ficha). A regra mora em 0004 e 0018.
// =====================================================================
import { dataISO, somarDias, dataCurta, noites } from './reservas.js';
import { digitos, formatarDocumento, tipoSugerido, tipoAoDigitar, validarHospede, idade, CAMPOS_FNRH, separarNome, errosDaFicha, fichaParaSalvar, residencia, LEGADO, ehLegado, MAX_GENERO_DESCRICAO } from './hospedes.js';
import { nomeDoPais } from './ibge.js';

export const MAX_PESSOAS = 3;

// O token vem no HASH da URL (#t=…): não vai para o servidor do site, nem
// para log, nem para Referer. 48 hex, e nada mais.
export function tokenDoHash(hash) {
  const m = /(?:^#|[#&])t=([0-9a-f]{48})(?:&|$)/.exec(String(hash || ''));
  return m ? m[1] : null;
}

export function montarLink(origem, token) {
  return `${String(origem || '').replace(/\/$/, '')}/precheckin.html#t=${token}`;
}

// ── 0021: os campos da FNRH, a chegada e a ficha de origem ───────────
// Os campos que o hóspede pode preencher por pessoa (Portaria MTur 177/2011,
// art. 3º: o que a ficha pede). Todos OPCIONAIS até o módulo `fnrh` decidir.
// A lista mora em lib/hospedes.js — são colunas de `hospedes`, e a recepção
// também as edita (0025). Aqui só se reexporta, para não haver duas listas.
export { CAMPOS_FNRH };
// ── 0065: AS LISTAS SÃO AS DA API DA FNRH ────────────────────────────
// A chave é o ID oficial do domínio (`/dominios/...`, docs/fnrh-api.md),
// na ordem da API. Decisão do Hugo, 26/09/2026: "IDs oficiais direto" —
// nenhuma tradução no envio. O rótulo é o da API, menos gênero e raça, que
// mantêm os da tela (a API diz "Outros", "Branco"; a pergunta aqui é
// "raça/cor", no feminino).
export const MOTIVOS_VIAGEM = Object.freeze([['COMPRAS', 'Compras'], ['CONGRESSO_FEIRA', 'Congresso/Feira'], ['ESTUDOS_CURSOS', 'Estudos/Cursos'],
  ['LAZER_FERIAS', 'Lazer/Férias'], ['NEGOCIOS', 'Negócios'], ['PARENTES_AMIGOS', 'Parentes/Amigos'], ['RELIGIAO', 'Religião'], ['SAUDE', 'Saúde']]);
export const MEIOS_TRANSPORTE = Object.freeze([['ONIBUS', 'Ônibus'], ['AUTOMOVEL', 'Automóvel'], ['AVIAO', 'Avião'], ['MOTO', 'Moto'],
  ['NAVIO_BARCO', 'Navio/Barco'], ['PE', 'A pé'], ['TREM', 'Trem'], ['BICICLETA', 'Bicicleta']]);
export const SEXOS = Object.freeze([['MULHER', 'Feminino'], ['HOMEM', 'Masculino'], ['OUTRO', 'Outro'], ['NAOINFORMADO', 'Prefiro não informar']]);
// raça/cor autodeclarada (0064) — dado SENSÍVEL: só com o módulo FNRH contratado
export const RACAS = Object.freeze([['BRANCA', 'Branca'], ['PRETA', 'Preta'], ['PARDA', 'Parda'], ['AMARELA', 'Amarela'], ['INDIGENA', 'Indígena'], ['NAOINFORMAR', 'Prefiro não informar']]);
// deficiência (0065) — dado de SAÚDE: a mesma trava da raça. O tipo só com "Sim", e obrigatório com ele.
export const DEFICIENCIAS = Object.freeze([['SIM', 'Sim'], ['NAO', 'Não'], ['NAOINFORMAR', 'Prefiro não informar']]);
export const TIPOS_DEFICIENCIA = Object.freeze([['FISICA', 'Física'], ['AUDITIVA_SURDEZ', 'Auditiva/Surdez'], ['VISUAL', 'Visual'],
  ['INTELECTUAL', 'Intelectual'], ['MULTIPLA', 'Múltipla']]);
// O LEGADO (chaves que a 0065 não converteu) e o limite da descrição do
// gênero moram em lib/hospedes.js — são regra das colunas de `hospedes`, e
// a régua da ficha completa, que mora lá, precisa deles.
export { LEGADO, ehLegado, MAX_GENERO_DESCRICAO };
const rotuloDe = (lista, campo) => v => (ehLegado(campo, v) ? `${LEGADO[campo][v]} (lista antiga)` : (lista.find(x => x[0] === v) || [])[1] || '');
export const rotuloSexo = rotuloDe(SEXOS);
export const rotuloRaca = rotuloDe(RACAS);
export const rotuloDeficiencia = rotuloDe(DEFICIENCIAS);
export const rotuloTipoDeficiencia = rotuloDe(TIPOS_DEFICIENCIA);
// Só o que está na lista oficial passa; o resto (legado, lixo) vira vazio
export const daLista = (lista, v) => (lista.some(x => x[0] === v) ? v : '');
export const FAIXAS_CHEGADA = Object.freeze([['antes_12', 'Antes das 12h'], ['12_15', 'Entre 12h e 15h'], ['15_18', 'Entre 15h e 18h'], ['depois_18', 'Depois das 18h']]);
// 0070: as UFs moram em ibge.js (com os países e os municípios). A lista
// NACIONALIDADES (gentílicos digitados) saiu: a nacionalidade é um país da
// tabela do IBGE, e a conversão da 0070 usa a lista velha só para ler o
// que já estava escrito.
export { UFS } from './ibge.js';
export const rotuloFaixa = f => (FAIXAS_CHEGADA.find(x => x[0] === f) || [])[1] || '';
export const rotuloMotivo = rotuloDe(MOTIVOS_VIAGEM, 'motivo_viagem');
export const rotuloTransporte = rotuloDe(MEIOS_TRANSPORTE, 'meio_transporte');

// A pessoa vazia do formulário. `hospede_id` e `documento_mascarado` só
// existem quando a pessoa veio da ficha (abrir_precheckin, 0021).
// 0070: país e município em código, o endereço partido; `uf` só filtra a
// lista de municípios. `mora_com_titular` é do acompanhante (Hugo,
// 29/09/2026: "mora com o titular", já marcado) — marcado, o endereço dele
// é o do titular, copiado no envio (`pessoasParaEnviar`).
export const CAMPOS_ENDERECO = Object.freeze(['residencia_pais', 'municipio_id', 'uf', 'cidade', 'cep', 'logradouro', 'numero_endereco', 'bairro', 'complemento']);
export const pessoaVazia = () => ({ nome: '', sobrenome: '', documento: '', tipo_documento: '', data_nascimento: '', telefone: '', email: '',
  nacionalidade_pais: '', residencia_pais: '', municipio_id: '', uf: '', cidade: '', cep: '', logradouro: '', numero_endereco: '',
  profissao: '', procedencia: '', destino: '', motivo_viagem: '', meio_transporte: '',
  nome_social: '', sexo: '', bairro: '', complemento: '', legalmente_incapaz: false, raca: '',
  genero_descricao: '', deficiencia: '', tipo_deficiencia: '',   // 0065
  mora_com_titular: true,
  hospede_id: null, documento_mascarado: null });

// O que o link devolve (abrir_precheckin.pessoas) vira o formulário
// pré-preenchido: o documento NÃO vem — só a máscara, para o hóspede
// reconhecer o seu e não redigitar.
export function pessoasDaFicha(pessoas) {
  const vazia = pessoaVazia();
  const lista = (pessoas || []).slice().sort((a, b) => Number(!!b.principal) - Number(!!a.principal))
    // `separarNome` por último, e ele é a TRAVA do nome dobrado.
    //
    // A 0042 já faz esse corte no banco. Isto aqui existe porque o link
    // do hóspede é uma página servida de um build, e build e banco não
    // andam juntos: um hóspede com a página velha no celular, ou um banco
    // ainda sem a 0042, receberia o nome completo no campo Nome com o
    // sobrenome ao lado — e enviar isso grava "Marina Costa Costa", sem
    // nenhum CHECK reclamando. A função é idempotente: sobre o que a 0042
    // devolve (já partido), ela não muda nada.
    .map(p => ({ ...vazia, ...Object.fromEntries(Object.entries(p).filter(([k]) => k in vazia).map(([k, v]) => [k, v ?? vazia[k]])) }))
    .map(p => ({ ...p, ...separarNome(p) }))
    // 0065: o legado nunca vira opção escolhida — o banco já devolve vazio
    // (abrir_precheckin); isto cobre banco e build fora de passo
    .map(p => ({ ...p, meio_transporte: daLista(MEIOS_TRANSPORTE, p.meio_transporte), motivo_viagem: daLista(MOTIVOS_VIAGEM, p.motivo_viagem) }))
    .map(p => ({ ...p, municipio_id: p.municipio_id == null ? '' : String(p.municipio_id) }));
  // 0070: o acompanhante que já tem endereço próprio na ficha, DIFERENTE do
  // do titular, abre com a caixa desmarcada — senão o envio o trocaria
  // pelo do titular sem ninguém ver
  const tit = lista[0];
  const mesmo = (a, b) => CAMPOS_ENDERECO.every(k => String(a[k] || '') === String(b[k] || ''));
  const temEndereco = p => CAMPOS_ENDERECO.some(k => String(p[k] || '').trim());
  lista.forEach((p, i) => { if (i > 0) p.mora_com_titular = !temEndereco(p) || mesmo(p, tit); });
  return lista.length ? lista.slice(0, MAX_PESSOAS) : [pessoaVazia()];
}

// O nome inteiro, para as telas que MOSTRAM a pessoa (o resumo, o
// recebido). Depois da 0041 `p.nome` e so o primeiro nome no
// formulario, e mostrar so ele seria um passo atras: a pessoa digitou
// os dois e a confirmacao mostraria metade.
export const nomeCompleto = p => [String((p || {}).nome || '').trim(), String((p || {}).sobrenome || '').trim()]
  .filter(Boolean).join(' ');

// A pessoa tem documento resolvido: digitado agora, ou já na ficha (máscara).
export const temDocumento = p => !!String(p.documento || '').trim() || (!!p.hospede_id && !!p.documento_mascarado);

// Uma pessoa está "preenchida" se tem algo além do que veio da ficha.
const CAMPOS_DIGITAVEIS = ['nome', 'sobrenome', 'documento', 'tipo_documento', 'data_nascimento', 'telefone', 'email', ...CAMPOS_FNRH];
const preenchida = p => CAMPOS_DIGITAVEIS.some(k => String(p[k] || '').trim()) || !!p.hospede_id;

// Validação do formulário do hóspede: erros por pessoa (índice → lista),
// mais os do conjunto. O titular (1ª) precisa de documento — é quem
// responde pela estadia; os outros podem completar no balcão. Desde 0021 o
// documento pode ser o que já está na ficha (não redigitado).
export function validarPessoas(pessoas, hoje) {
  const lista = (pessoas || []).filter(preenchida);
  const erros = { geral: [], porPessoa: {} };
  if (!lista.length) erros.geral.push('Informe pelo menos quem é o titular');
  if (lista.length > MAX_PESSOAS) erros.geral.push(`No máximo ${MAX_PESSOAS} pessoas por quarto`);
  lista.forEach((p, i) => { const e = errosDaPessoa(p, i, hoje, lista); if (e.length) erros.porPessoa[i] = e; });
  return { ok: !erros.geral.length && !Object.keys(erros.porPessoa).length, lista, ...erros };
}

// A validação de UMA pessoa (a etapa dela), com o mesmo critério — mesmo
// que ela esteja vazia (é a etapa pedindo o que falta).
// Menor de idade NA DATA DO HOTEL. A mesma conta da 0017/0020 e da 0041 —
// e por isso a data vem de fora, em vez de `new Date()`: o dia do hotel é
// o que o banco usa, e a tela que usar outro recusa quem o banco aceita.
export function ehMenor(p, hoje) {
  const n = String((p || {}).data_nascimento || '').trim();
  if (!n || !hoje) return false;
  const [a, m, d] = String(hoje).split('-').map(Number);
  const limite = new Date(Date.UTC(a - 18, (m || 1) - 1, d || 1)).toISOString().slice(0, 10);
  return n > limite;
}

function errosDaPessoa(p, i, hoje, lista = null) {
  const e = validarHospede({ ...p, tipo_documento: p.tipo_documento || tipoSugerido(p.documento) || null }, hoje);
  // ── DOCUMENTO DE CADA UM (0041, decisão 2a do Hugo) ────────────────
  // Até aqui só o titular era cobrado na tela, e o banco cobrava o mesmo.
  // A 0041 passou a exigir de todos, com UMA exceção: menor acompanhado
  // de titular documentado. Esta tela repete a regra do banco para o erro
  // aparecer ANTES de o hóspede apertar enviar — se ela recusasse o que o
  // banco aceita (ou o contrário), o link viraria adivinhação.
  if (i === 0) {
    if (!temDocumento(p)) e.push('O titular precisa informar o documento');
  } else if (!temDocumento(p)) {
    const titular = Array.isArray(lista) ? lista[0] : null;
    if (!(ehMenor(p, hoje) && titular && temDocumento(titular))) {
      e.push('Informe o documento (CPF ou passaporte). Só menor de idade entra sem, acompanhado do titular');
    }
  }
  // O sobrenome saiu daqui em 19/09: a regra do Hugo é "sempre nome e
  // sobrenome, em qualquer situação", e por isso ela mora em
  // `validarHospede` (lib/hospedes.js), que TODAS as telas chamam. Aqui
  // ficaria valendo só para o link — e o balcão continuaria aceitando
  // "Maria".
  // 0070: o CEP (só o brasileiro tem regra) está em `errosDaFicha`
  e.push(...errosDaFicha(p));   // 0065: gênero "Outro" e deficiência — a mesma régua do balcão
  return e;
}
// `lista` e opcional: a etapa de UMA pessoa nao conhece o titular, e sem
// ele o acompanhante sem documento e recusado -- que e o lado seguro.
export function validarPessoa(p, i, hoje, lista = null) { return errosDaPessoa(p || pessoaVazia(), i, hoje, lista); }

// A placa: só letras e dígitos, maiúscula (ABC1D23 / ABC1234); vazia vale.
export function limparPlaca(placa) { return String(placa || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }
export function validarReserva(r) {
  const erros = [];
  const placa = limparPlaca(r && r.placa_veiculo);
  if (placa && !/^[A-Z0-9]{5,8}$/.test(placa)) erros.push('Placa inválida');
  if (r && r.chegada_prevista && !FAIXAS_CHEGADA.some(f => f[0] === r.chegada_prevista)) erros.push('Faixa de chegada inválida');
  return erros;
}

const limpo = v => String(v || '').trim() || null;
// O que vai para a função de servidor: só os campos aceitos, limpos. O
// documento mascarado NUNCA vai (não é documento); a ficha de origem vai
// pelo id.
export function pessoasParaEnviar(lista) {
  const titular = (lista || [])[0] || {};
  return lista.map((p0, i) => {
    // 0070: o acompanhante que mora com o titular leva o endereço DELE
    const p = i > 0 && p0.mora_com_titular !== false
      ? { ...p0, ...Object.fromEntries(CAMPOS_ENDERECO.map(k => [k, titular[k]])) } : p0;
    // município só de quem mora no Brasil; cidade digitada só de quem mora fora
    const end = fichaParaSalvar(p);
    const documento = limpo(p.documento);
    const tipo = documento ? (p.tipo_documento || tipoSugerido(p.documento) || null) : null;
    return { nome: String(p.nome || '').trim(), sobrenome: String(p.sobrenome || '').trim() || null, documento, tipo_documento: tipo,
      data_nascimento: p.data_nascimento || null, telefone: limpo(p.telefone), email: String(p.email || '').trim().toLowerCase() || null,
      hospede_id: p.hospede_id || null,
      // 0070
      nacionalidade_pais: limpo(p.nacionalidade_pais), residencia_pais: limpo(end.residencia_pais),
      municipio_id: limpo(end.municipio_id) ? Number(end.municipio_id) : null, cidade: limpo(end.cidade),
      cep: limpo(end.cep), logradouro: limpo(end.logradouro), numero_endereco: limpo(end.numero_endereco),
      profissao: limpo(p.profissao), procedencia: limpo(p.procedencia), destino: limpo(p.destino),
      // 0065: só o que está na lista da API; o legado não vai (o banco recusaria)
      motivo_viagem: daLista(MOTIVOS_VIAGEM, p.motivo_viagem) || null, meio_transporte: daLista(MEIOS_TRANSPORTE, p.meio_transporte) || null,
      // 0062
      nome_social: limpo(p.nome_social), sexo: limpo(p.sexo), bairro: limpo(end.bairro), complemento: limpo(end.complemento),
      legalmente_incapaz: !!p.legalmente_incapaz,
      raca: limpo(p.raca),   // 0064
      // 0065: a descrição só com OUTRO; o tipo só com SIM — o par vai coerente ou não vai
      genero_descricao: p.sexo === 'OUTRO' ? limpo(p.genero_descricao) : null,
      deficiencia: limpo(p.deficiencia), tipo_deficiencia: p.deficiencia === 'SIM' ? limpo(p.tipo_deficiencia) : null };
  });
}
export function reservaParaEnviar(r) {
  return { chegada_prevista: (r && r.chegada_prevista) || null, placa_veiculo: limparPlaca(r && r.placa_veiculo) || null };
}

// As etapas da página do hóspede (esboço aprovado 15/09/2026):
// boas-vindas → 1 Você → 2 Sua viagem → 3 Acompanhantes → 4 Confirmar.
export const ETAPAS = Object.freeze(['voce', 'viagem', 'acompanhantes', 'confirmar']);
export function progresso(etapa) { const i = ETAPAS.indexOf(etapa); return i < 0 ? 0 : Math.round(((i + 1) / ETAPAS.length) * 100); }

// "Bom dia" / "Boa tarde" / "Boa noite" pela hora LOCAL do aparelho — é
// cortesia, não regra; o dia do hotel não entra aqui.
export function saudacao(hora, idioma) {
  const h = Number(hora);
  if (idioma === 'en') return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

// ── a recepção ───────────────────────────────────────────────────────
export const ESTADOS = Object.freeze({
  sem_link:   { rotulo: 'Sem link',        classe: 'badge-gray' },
  link:       { rotulo: 'Link enviado',    classe: 'badge-blue' },
  vencido:    { rotulo: 'Link vencido',    classe: 'badge-red' },
  preenchido: { rotulo: 'Preenchido',      classe: 'badge-yellow' },
  conferido:  { rotulo: 'Conferido',       classe: 'badge-green' },
  recusado:   { rotulo: 'Recusado',        classe: 'badge-red' },
});

// O estado de uma linha do painel (precheckin_painel), visto de agora.
export function estadoDaReserva(l, agoraISO) {
  if (l.envio_situacao === 'pendente') return 'preenchido';
  if (l.envio_situacao === 'conferido') return 'conferido';
  if (l.envio_situacao === 'recusado') {
    // recusado, mas já há um link novo vivo → volta a "link enviado"
    if (l.convite_id && !l.convite_usado_em && l.convite_expira_em > agoraISO && l.convite_criado_em > (l.envio_enviado_em || '')) return 'link';
    return 'recusado';
  }
  if (!l.convite_id) return 'sem_link';
  if (l.convite_usado_em) return 'preenchido';
  if (l.convite_expira_em <= agoraISO) return 'vencido';
  return 'link';
}

export const ABAS_PRECHECKIN = Object.freeze([
  ['pendentes', 'Para conferir', ['preenchido']],
  ['sem_link', 'Sem link', ['sem_link', 'vencido']],
  ['enviados', 'Aguardando hóspede', ['link', 'recusado']],
  ['todas', 'Todas', ['sem_link', 'link', 'vencido', 'preenchido', 'conferido', 'recusado']],
]);

const chaveQuarto = n => { const m = /^(\d+)(.*)$/.exec(String(n || '')); return m ? m[1].padStart(6, '0') + m[2].toLowerCase() : 'z' + String(n || '').toLowerCase(); };
const semAcento = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function classificarPainel(linhas, agoraISO) {
  const ordem = ['preenchido', 'sem_link', 'vencido', 'link', 'recusado', 'conferido'];
  return (linhas || []).map(l => ({ ...l, estado: estadoDaReserva(l, agoraISO) }))
    .sort((a, b) => (ordem.indexOf(a.estado) - ordem.indexOf(b.estado)) || a.check_in.localeCompare(b.check_in) || chaveQuarto(a.quarto).localeCompare(chaveQuarto(b.quarto)));
}

export function filtrarPainel(lista, { aba, texto }) {
  const def = ABAS_PRECHECKIN.find(a => a[0] === aba) || ABAS_PRECHECKIN[0];
  const t = semAcento(texto).trim();
  return (lista || []).filter(l => t ? [l.quarto, l.numero, l.titular, l.imobiliaria].some(c => semAcento(c).includes(t)) : def[2].includes(l.estado));
}

export function resumoPainel(lista) {
  const n = e => (lista || []).filter(l => l.estado === e).length;
  return { pendentes: n('preenchido'), semLink: n('sem_link') + n('vencido'), aguardando: n('link') + n('recusado'), conferidos: n('conferido') };
}

// Quem gera o link (0059, Hugo 24/09): "a recepção não gera link de
// pré-check-in — quem gera e envia é o proprietário ou a imobiliária".
// `souDono`: quem está logado é uma imobiliária (ou o Administrador do
// Sistema). A equipe do hotel só gera para reserva SEM imobiliária — a
// do próprio hotel —, senão ninguém geraria. Quem recusa de verdade é o
// banco (`criar_convite_precheckin`); aqui é só não oferecer o botão.
export function podeGerarLink(l, { souDono }) {
  return !!souDono || !(l && l.imobiliaria);
}

// O que a pessoa pode fazer numa linha. Sem "recusar" (0059): se o envio
// tem erro, a recepção aprova e corrige a ficha no check-in.
export function acoesDaLinha(l, { podeEditar, souDono = false }) {
  if (!podeEditar) return [];
  const a = [];
  if (l.estado === 'preenchido') a.push('conferir');
  if (podeGerarLink(l, { souDono }) && ['sem_link', 'vencido', 'link', 'recusado', 'conferido'].includes(l.estado)) {
    a.push(l.estado === 'sem_link' || l.estado === 'vencido' ? 'gerar' : 'novo_link');
  }
  return a;
}

// A comparação lado a lado: o que o hóspede mandou × o que já está na
// ficha. Mesmo documento = mesma pessoa (é o que 0018 usa para não
// duplicar); sem documento = pessoa nova.
export function compararEnvio(pessoas, hospedes) {
  const fichas = hospedes || [];
  return (pessoas || []).slice().sort((a, b) => a.ordem - b.ordem).map(p => {
    const d = digitos(p.documento) || (p.documento || '').trim();
    // mesmo documento = mesma pessoa; sem documento redigitado, a ficha de
    // origem (hospede_id, 0021) diz quem é
    const igual = (d ? fichas.find(h => (digitos(h.documento) || h.documento || '') === d) : null)
      || (p.hospede_id ? fichas.find(h => h.hospede_id === p.hospede_id) : null) || null;
    const ROTULO = { nome: 'nome', documento: 'documento', telefone: 'telefone', email: 'e-mail', data_nascimento: 'nascimento',
      // 0070
      nacionalidade_pais: 'nacionalidade', residencia_pais: 'país onde mora', municipio_id: 'município', cidade: 'cidade',
      logradouro: 'logradouro', numero_endereco: 'número', profissao: 'profissão', cep: 'CEP',
      procedencia: 'procedência', destino: 'destino', motivo_viagem: 'motivo', meio_transporte: 'transporte',
      // 0063: os campos da FNRH (0062)
      nome_social: 'nome social', sexo: 'gênero', raca: 'raça/cor', bairro: 'bairro', complemento: 'complemento', legalmente_incapaz: 'legalmente incapaz',
      // 0065
      genero_descricao: 'descrição do gênero', deficiencia: 'deficiência', tipo_deficiencia: 'tipo de deficiência' };
    // o incapaz é booleano e a conferência faz OR (0062): só "muda" quando o
    // envio marca e a ficha ainda não — desmarcado no link não desfaz a ficha
    const difere = k => k === 'legalmente_incapaz' ? (!!p[k] && !igual[k]) : k === 'documento' ? (d && d !== (digitos(igual.documento) || igual.documento || '')) : String(p[k] || '').trim() && String(p[k] || '').trim() !== String(igual[k] || '').trim();
    return { ...p, jaNaFicha: igual, novo: !igual,
      mudou: igual ? Object.keys(ROTULO).filter(difere).map(k => ROTULO[k]) : [] };
  });
}

// A linha "viagem" de uma pessoa, para a recepção ler de uma vez.
export function resumoViagem(p) {
  // 0070: o país pelo nome do IBGE; onde mora, pelo município (o nome
  // vem do banco em `municipio`) ou pela cidade de fora
  return [nomeDoPais(p.nacionalidade_pais) || p.nacionalidade || null, p.profissao, residencia(p) || null,
    rotuloMotivo(p.motivo_viagem) || null, rotuloTransporte(p.meio_transporte) || null,
    p.procedencia ? `de ${p.procedencia}` : null, p.destino ? `→ ${p.destino}` : null].filter(Boolean).join(' · ');
}

export { dataISO, somarDias, dataCurta, noites, formatarDocumento, tipoSugerido, tipoAoDigitar, digitos, idade };
